import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 共用掃描 lookup：Enter／lock／focus／0-1-多筆分流。
 * 不綁 toast、音效、IMS API；lookup／onPick／resolveAmbiguous 由呼叫端注入。
 *
 * lookup(code) => Promise<{ items: T[] }>
 * 多筆時 await resolveAmbiguous(items, code) → 再 onPick(selected)。
 */
function useScanLookup({
  lookup,
  onPick,
  onEmpty,
  onError,
  resolveAmbiguous,
  enabled = true,
} = {}) {
  const [scanCode, setScanCode] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const inputRef = useRef(null);
  const lockRef = useRef(false);
  const optionsRef = useRef({
    lookup,
    onPick,
    onEmpty,
    onError,
    resolveAmbiguous,
    enabled,
  });
  const scanCodeRef = useRef(scanCode);

  useEffect(() => {
    optionsRef.current = {
      lookup,
      onPick,
      onEmpty,
      onError,
      resolveAmbiguous,
      enabled,
    };
  });

  useEffect(() => {
    scanCodeRef.current = scanCode;
  }, [scanCode]);

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus?.();
    });
  }, []);

  const runLookup = useCallback(async () => {
    const options = optionsRef.current;
    if (!options.enabled || lockRef.current) {
      return;
    }

    const code = String(scanCodeRef.current ?? '').trim();
    if (!code) {
      return;
    }
    if (typeof options.lookup !== 'function') {
      return;
    }

    lockRef.current = true;
    setIsLookingUp(true);
    let shouldFocus = false;
    let shouldClearCode = false;

    try {
      const result = await options.lookup(code);
      if (!optionsRef.current.enabled) {
        return;
      }
      const items = Array.isArray(result?.items) ? result.items : [];

      if (items.length === 0) {
        await options.onEmpty?.(code);
        shouldClearCode = true;
        shouldFocus = true;
        return;
      }

      if (items.length === 1) {
        await options.onPick?.(items[0], { code, items });
        shouldClearCode = true;
        shouldFocus = true;
        return;
      }

      setIsLookingUp(false);
      const selected = typeof options.resolveAmbiguous === 'function'
        ? await options.resolveAmbiguous(items, code)
        : null;
      if (!optionsRef.current.enabled) {
        return;
      }
      if (selected) {
        await options.onPick?.(selected, { code, items });
        shouldClearCode = true;
        shouldFocus = true;
      }
    } catch (error) {
      setIsLookingUp(false);
      await options.onError?.(error, code);
      shouldClearCode = true;
      shouldFocus = true;
    } finally {
      lockRef.current = false;
      setIsLookingUp(false);
      if (shouldClearCode) {
        setScanCode('');
      }
      if (shouldFocus) {
        focusInput();
      }
    }
  }, [focusInput]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      runLookup();
    }
  }, [runLookup]);

  return {
    scanCode,
    setScanCode,
    isLookingUp,
    inputRef,
    focusInput,
    runLookup,
    handleKeyDown,
  };
}

export default useScanLookup;
